/**
 * arbt-nav-disclosure.js — ARB-318, keyboard access to the desktop mega menu.
 *
 * The reviewer's finding on /collections/power-carving: you can Tab past a top-level nav
 * item without opening its mega panel (good), but there is no way to OPEN it — Enter goes
 * to the page. The <summary> was the whole control, so it could only ever do one of the
 * two jobs, and which one depended on the section's "Open dropdown items on..." setting.
 *
 * DESIGN.md §5 (Interaction Patterns → Disclosure) fixes the shape: the parent link stays
 * a link, and the disclosure is a separate adjacent <button> with aria-expanded /
 * aria-controls, Enter and Space to open, Escape to close and return focus. This file is
 * the behaviour half; sections/header.liquid emits the markup and explains why the link
 * and the button sit OUTSIDE the <details>.
 *
 * What this file deliberately does NOT reimplement, because MegaMenuDisclosure in
 * theme.js already owns it and a second copy would drift:
 *   - the open/close transition, and closing an already-open sibling  (`_transition`)
 *   - hover-to-open on a fine pointer, with its 500ms tolerance       (`_detectHover`)
 *   - click-outside-to-close in "open on click" mode                 (`_detectClickOutside`)
 *   - close-on-focusout, which is what makes Tab off the panel's last link close it
 * So the only levers used here are `details.open` — the custom element's own setter, not
 * the `open` attribute, which would skip both the transition and the sibling close — and
 * a re-dispatch of mouseover/mouseout onto the <details> so that hovering the label or
 * the chevron still reaches the theme's one hover implementation.
 */
(() => {
  const ITEM = '.header__nav-item';
  const BUTTON = '.header__nav-disclosure';
  const PANEL = 'details.header__nav-panel';

  /** Keep aria-expanded and the underline class honest however the panel was opened. */
  const sync = (item) => {
    const details = item.querySelector(`:scope > ${PANEL}`);
    const button = item.querySelector(`:scope > ${BUTTON}`);
    if (!details || !button) return;
    const open = details.hasAttribute('open');
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    item.classList.toggle('header__nav-item--open', open);
  };

  const bind = (root) => {
    for (const item of (root || document).querySelectorAll(ITEM)) {
      if (item.dataset.arbtNavBound === 'true') continue;

      const details = item.querySelector(`:scope > ${PANEL}`);
      const button = item.querySelector(`:scope > ${BUTTON}`);
      if (!details || !button) continue;
      item.dataset.arbtNavBound = 'true';

      // Enter and Space on a <button> both dispatch click, so one handler is the whole
      // keyboard contract. stopPropagation matters: in "open on click" mode theme.js
      // adds a document click listener the moment the panel opens, and the button is
      // outside the <details>, so the same click would immediately read as a click
      // outside and close it again.
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        details.open = !details.open;
        sync(item);
      });

      // Escape closes and returns focus to the button. theme.js already closes on
      // Escape, but leaves focus wherever it was, which loses the keyboard user. The
      // panel is inside this item, so this covers Escape from the button and from any
      // link in the panel.
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!details.hasAttribute('open')) return;
        event.stopPropagation();
        details.open = false;
        sync(item);
        button.focus();
      });

      // Pointer parity. The <details> used to wrap the label and the chevron, so
      // theme.js's own mouseover/mouseout listeners covered the whole item. They now
      // cover the panel only, so re-dispatch onto the <details> for the rest of the
      // item — which keeps _detectHover, its trigger check and its 500ms close
      // tolerance as the single hover implementation instead of a rival timer here.
      const relay = (type) => (event) => {
        const other = type === 'mouseover' ? event.target : event.relatedTarget;
        if (other && details.contains(other)) return; // theme.js already sees this one
        details.dispatchEvent(new MouseEvent(type, { bubbles: false }));
      };
      item.addEventListener('mouseover', relay('mouseover'));
      item.addEventListener('mouseout', relay('mouseout'));

      // The attribute is the state: hover, Escape, a theme-editor block select and our
      // own button all end up here, so aria-expanded is read off it rather than set at
      // each call site.
      new MutationObserver(() => sync(item)).observe(details, {
        attributes: true,
        attributeFilter: ['open'],
      });
      sync(item);
    }
  };

  const start = () => bind(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // The header is a section; a Customize save re-renders it and drops these listeners.
  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();
